/* eslint-disable @typescript-eslint/no-explicit-any -- Firestore/Supabase interop requires dynamic typing for refs and sanitization */
/**
 * Sales Domain Module
 *
 * Handles all sales-related operations including:
 * - Creating, updating, and deleting sales
 * - Returns management
 * - Sales statistics and aggregations
 * - Cart item processing
 *
 * This module demonstrates the proper TypeScript approach for Firebase modules.
 */

import { format } from 'date-fns'
import { getDefaultStore } from 'jotai'
import * as Sentry from '@sentry/react-native'

// Firebase primitives (direct import to avoid circular deps)
import { rootDb, fieldValue } from './firebaseFirestore'

// Firestore sanitizer + cart-line slimmer extracted to their own module so they can be
// unit-tested in isolation (importing sales.ts pulls in Sentry/react-native — see salesSanitize.test.js).
import { sanitizeForFirestore, stripHeavyProductCartFields } from './salesSanitize'

// Shared types and utilities
import {
  shouldUseSupabase,
  filterUndefined,
  transformApiItem,
  snapshotExists,
  toDate,
  type DateRange,
  type TimestampLike,
  type MockRef,
} from './types'
import { dualWriteUpdate, saveUpdateRecord } from './updates'
import { bestEffortDualWrite, bestEffortDualWriteDelete, type DualWriteOutcome } from './dualWrite'
import { isMirrorIntentActive, persistMirrorIntent } from './mirrorIntent'
import { getFirestoreRef, runIdempotentDelete } from './firestoreRefUtils'
import { tagPseudoLineForApi, isPseudoLine } from './cartLineType'
import { chargedLineAmount } from './cartLineAmount'
import { stockBaselineTracker } from './stockBaseline'
import { saleStockEffects, saleStockLines } from './saleStockEffects'
import {
  LOCAL_EFFECTS_VERSION,
  assertLocalEffects,
  businessDayOfDailyStatId,
  isoInstantOf,
  rawIncrements,
  toDisplayObject,
  type DailyStatEffect,
  type LocalEffects,
} from '../Write/localEffects'
import { writeW, type WAckDisposition } from '../Write/writeW'
import { buildOutboxRequest } from '../Write/outboxRequest'

import type {
  FirestoreDb,
  FirestoreQuery,
  FiscalInfo,
  Organization,
} from './firestore.types'

import {
  dbAtom,
  organizationAtom,
  productsDictAtom,
  storesDictAtom,
  constantsAtom,
} from '../AtomsProvider'

import { dailyStatIDForRecord, getLocalStoreDate, setTimezone } from '../../utils/timezoneFunctions'
import { salesApi, auditLogApi } from '../API/typedApiClient'
import { captureWriteRequest, createSalesApi } from '@atlas/api-client'
import { readWholeList, newestFirst } from '../API/readWholeList'
import { saveFiscalReturn, loadLKTokensFromCache, checkLKTokens, setNewLKTokens } from '../lightkassa'
import { saveReturnFromDelete, refreshCredentials } from '../wipon'
import { commitWithRedeliveryGuard, writeClaimGuard } from './writeClaims'

// ============================================================================
// Types
// ============================================================================

/**
 * Payment method with wallet configuration per store
 */
export interface PaymentMethod {
  id: string
  name?: string
  value?: number
  comission?: number
  destinationWalletForStore: Record<string, { id: string; name?: string }>
}

/**
 * Purchase method (e.g., in-store, online)
 */
export interface PurchaseMethod {
  id: string
  name?: string
}

/**
 * Client information for a sale
 */
export interface SaleClient {
  id: string
  name?: string
  phoneNumber?: string
  bonusValue?: number
  punchCardValue?: number
}

/**
 * Cart item in a sale
 */
export interface CartItem {
  id: string
  name: string
  quantity: number
  price?: number
  finalItemPrice?: number
  itemPriceWithoutModifiers?: number
  purchasePrice?: number
  packagePrice?: number
  salePrices?: Record<string, number>
  ref?: MockRef
  cartItemId?: string
  cartItemRef?: MockRef
  saleID?: string
  store?: string
  saleDate?: Date | TimestampLike
  clientId?: string | null
  clientPhone?: string | null
  categoryID?: string
  supplierID?: string
  variant?: unknown
  category?: unknown
  punchCardEnabled?: boolean
  isComposite?: boolean
  manufacturedBeforeSale?: boolean
  // Return-related fields
  isReturned?: boolean
  returnedQuantity?: number
  returnedAmount?: number
  returnReason?: string
  ingredients?: Array<{
    id: string
    name: string
    quantity: number
    ref?: MockRef
  }>
  product?: {
    name?: string
    purchasePrice?: number
  }
}

/**
 * Fiscal information from OFD providers — the operator's receipt, verbatim. One definition, in
 * firestore.types; the interface that stood here named `receiptNumber`/`qrCode`, keys no operator
 * sends.
 */
export type { FiscalInfo }

/**
 * Return information for a sale
 */
export interface ReturnInfo {
  returnType: 0 | 1 // 0 = exchange, 1 = money back
  returnMethod?: PaymentMethod
  totalReturnedAmount: number
  dailyStatID?: string
  returnedItems?: CartItem[]
  [key: string]: unknown
}

/**
 * Complete sale information
 */
export interface Sale {
  id?: string
  ref?: MockRef
  store: string
  organizationId: string
  saleDate: Date | TimestampLike
  dailyStatID: string
  cartItems: CartItem[]
  totalCartPrice?: number
  finalTotalCartPrice: number
  totalPurchasePrice?: number
  discountAmount?: number
  discountPercentage?: number

  // Client & loyalty
  client?: SaleClient | null
  clientId?: string | null
  clientPhone?: string | null
  bonusAccumulation?: number
  bonusReduction?: number
  bonusAction?: number
  cashbackEnabled?: boolean
  punchCardEnabled?: boolean
  punchCardReduction?: number
  punchCardCapacity?: number

  // Payment
  paymentMethod?: PaymentMethod
  secondPaymentMethod?: PaymentMethod
  transactionId?: string
  secondTransactionId?: string
  purchaseMethod: PurchaseMethod

  // Cashier
  cashierUID: string
  createdBy?: string

  // Optional details
  comment?: string
  tableNumber?: string | number
  priceType?: { id: string } | string
  modifier?: unknown
  modifierComment?: string

  // Delivery
  delivery?: boolean
  deliveryPrice?: number
  deliveryPaidByCustomer?: boolean
  deliveryPaymentSourceWallet?: string
  deliverySpendingId?: string

  // Fiscal
  fiscalInfo?: FiscalInfo
  returnInfo?: ReturnInfo
  returnId?: string
  returnFiscalInfo?: FiscalInfo
}

/**
 * Sale grouped by date for display
 */
export interface SalesByDate {
  title: string
  data: Sale[]
}

/**
 * Parameters for fetching sales
 */
export interface GetSalesParams {
  startDate?: string | Date
  endDate?: string | Date
  storeId?: string
  clientId?: string
  limit?: number
  offset?: number
}

/**
 * Client sales statistics
 */
export interface ClientStatistics {
  salesCount: number
  totalRevenue: number
  totalCost: number
  totalProfit: number
}

/**
 * Product statistics from client sales
 */
export interface ProductStats {
  name: string
  categoryID?: string
  supplierID?: string
  saleQuantity: number
  saleAmount: number
  checkQuantity: number
  purchaseAmount: number
  packageAmount: number
  margin?: number
  profit?: number
  writeOffAmount?: number
}

/**
 * Daily stat increments for sales
 */
export interface DailyStatIncrements {
  saleAmount?: number | ReturnType<typeof fieldValue.increment>
  saleQuantity?: number | ReturnType<typeof fieldValue.increment>
  salesPurchaseAmount?: number | ReturnType<typeof fieldValue.increment>
  bonusClients?: number | ReturnType<typeof fieldValue.increment>
  bonusAccumulation?: number | ReturnType<typeof fieldValue.increment>
  bonusReduction?: number | ReturnType<typeof fieldValue.increment>
  punchCardReduction?: number | ReturnType<typeof fieldValue.increment>
  bankPercent?: number | ReturnType<typeof fieldValue.increment>
  [key: string]: number | ReturnType<typeof fieldValue.increment> | undefined
}

// ============================================================================
// Helper Functions
// ============================================================================


/**
 * ATL-871 — A `NaN` NUMERIC ON ONE CART LINE COSTS THE WHOLE SALE ITS SUPABASE ROW, FOREVER.
 *
 * Firestore stores IEEE-754 doubles and therefore stores `NaN` quite happily. The API cannot: the
 * generated Zod request schema types these fields as `number()`, and `NaN` fails it — client-side,
 * before the request is ever sent. `bestEffortDualWrite` classifies a request-validation rejection
 * as a DETERMINISTIC defect, which is correct (replaying the same payload would fail identically),
 * and deterministic defects are never queued in the outbox. So the sale commits to Firestore and
 * its Supabase leg is refused permanently, with no retry and no reconciliation — the sale, its
 * revenue, its transaction and its stock movements are simply absent from Supabase.
 *
 * THE SOURCE IS UPSTREAM AND IS NOT FIXED HERE. Prod, 2026-08-06: 73 PRODUCT documents across the
 * 29 organizations short of sales carry `purchasePrice: NaN` or `packagePrice: NaN` in Firestore
 * (e.g. электро/Сосок, Aladdin/Доставка, and 22 composites at ресторан Марракеш). Every cart line
 * built from one of those products inherits the NaN, so every sale containing it is refused. That
 * product data needs its own repair — this function cannot invent a purchase price.
 *
 * WHY DROP RATHER THAN SUBSTITUTE. Writing `0` in place of an unknown cost would fabricate a COGS
 * figure and understate cost of goods in the P&L — the exact class of thing this repo forbids.
 * DROPPING the key sends "unknown", which is precisely what `NaN` means, and it is already how
 * every other consumer of the field reads it (`purchasePrice || 0` at the aggregate sites, and the
 * column is nullable). Between "one cost field is unknown in Supabase, as it is in Firestore" and
 * "the entire sale is missing from Supabase", the first is strictly less wrong.
 *
 * It is LOUD, once per (field, product), so the underlying product defect stays visible instead of
 * being laundered by this function.
 */
const dropNonFiniteNumbers = (out: any): void => {
  for (const key of Object.keys(out)) {
    const v = out[key]
    if (typeof v !== 'number' || Number.isFinite(v)) continue
    delete out[key]
    Sentry.captureMessage(`cart item carries a non-finite ${key} — dropped from the Supabase payload`, {
      level: 'warning',
      tags: { cart_item_non_finite: key },
      fingerprint: ['cart-item-non-finite', key, String(out.id ?? 'unknown')],
      contexts: {
        cart_item_non_finite: { field: key, productId: out.id ?? null, name: out.name ?? null },
      },
    })
  }
}

/**
 * Coerce a cart item to the shape the sale API (POST /sales) expects (ATL-481).
 *
 * When reads are on Firestore (launch posture), cart items inherit RAW Firestore product fields:
 * `barcode` as a number and audit dates (`createdAt`/`updatedAt`) as Firestore Timestamp/Date
 * OBJECTS. The sale DTO's cart-item schema types these as optional STRINGS, so the typed client's
 * request validation rejects the dual-write payload → the sale errors. (In Supabase-read mode the
 * product already arrives API-shaped — string barcode, ISO dates — so this is a no-op.)
 */
export const coerceCartItemForApi = (item: any): any => {
  if (!item || typeof item !== 'object') return item
  const out: any = { ...item }
  if (typeof out.barcode === 'number') out.barcode = String(out.barcode)
  for (const f of ['createdAt', 'updatedAt']) {
    const v = out[f]
    if (v && typeof v === 'object') {
      if (typeof v.toDate === 'function') out[f] = v.toDate().toISOString()
      else if (v instanceof Date) out[f] = v.toISOString()
      else delete out[f] // unrecognized object → drop rather than send an invalid type
    }
  }
  dropNonFiniteNumbers(out)
  // Custom/delivery lines: swap the shared sentinel id for the unique cartItemId and
  // tag the kind — Supabase payload only; the Firestore write keeps the legacy shape.
  // NOTE: requires the lineType-aware backend (same release) — do not cherry-pick alone.
  return tagPseudoLineForApi(out)
}

/**
 * Get Jotai store and database reference
 */
const getDbContext = () => {
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom) as FirestoreDb
  const organization = jotaiStore.get(organizationAtom) as Organization | null
  return { jotaiStore, db, organization, organizationId: organization?.id }
}

// API responses are already Firestore-format (camelCase). No field mapping here.
// Cart item price normalization is handled by the backend (Principle #2).

/**
 * Group sales by date for display
 */
const groupSalesByDate = (sales: Sale[], storeId?: string): SalesByDate[] => {
  const grouped: Record<string, Sale[]> = {}

  sales.forEach((sale) => {
    const saleDate = sale.saleDate && typeof (sale.saleDate as TimestampLike).toDate === 'function'
      ? (sale.saleDate as TimestampLike).toDate()
      : new Date(sale.saleDate as string | Date)
    const dateKey = format(getLocalStoreDate(sale.store || storeId || '', saleDate), 'd/M/y EEEE')

    if (!grouped[dateKey]) {
      grouped[dateKey] = []
    }
    grouped[dateKey].push(sale)
  })

  return Object.entries(grouped).map(([title, data]) => ({ title, data }))
}

// ============================================================================
// Stat Increment Functions
// ============================================================================

/**
 * Calculate daily stat increments for sales
 * @param sales - Array of sales to process
 * @param forFirebase - If true, returns fieldValue.increment; otherwise raw numbers
 * @param negative - If true, inverts all values (for deletions)
 */
export const dailyStatIncrementsForSales = (
  sales: Sale[],
  forFirebase: boolean,
  negative: boolean
): DailyStatIncrements => {
  const saleStatFields: Record<string, number> = {}

  for (const info of sales) {
    saleStatFields['saleAmount'] = (saleStatFields['saleAmount'] || 0) + (info.finalTotalCartPrice || 0)
    saleStatFields['saleQuantity'] = (saleStatFields['saleQuantity'] || 0) + 1
    saleStatFields['salesPurchaseAmount'] = (saleStatFields['salesPurchaseAmount'] || 0) + (info.totalPurchasePrice || 0)

    if (info.client != null && info.paymentMethod) {
      saleStatFields['bonusClients'] = (saleStatFields['bonusClients'] || 0) + 1
      saleStatFields['bonusAccumulation'] = (saleStatFields['bonusAccumulation'] || 0) + (info.bonusAccumulation || 0)
      saleStatFields['bonusReduction'] = (saleStatFields['bonusReduction'] || 0) + (info.bonusReduction || 0)
      saleStatFields['punchCardReduction'] = (saleStatFields['punchCardReduction'] || 0) + (info.punchCardReduction || 0)
    }

    // Payment methods and wallets
    if (info.paymentMethod) {
      const pmId = info.paymentMethod.id
      const value = info.paymentMethod.value || info.finalTotalCartPrice || 0
      const walletId = info.paymentMethod.destinationWalletForStore?.[info.store]?.id

      saleStatFields[`${pmId}SalesAmount`] = (saleStatFields[`${pmId}SalesAmount`] || 0) + value
      saleStatFields[`${pmId}SalesQuantity`] = (saleStatFields[`${pmId}SalesQuantity`] || 0) + 1

      if (walletId) {
        saleStatFields[`${walletId}SalesAmount`] = (saleStatFields[`${walletId}SalesAmount`] || 0) + value
        saleStatFields[`${walletId}SalesQuantity`] = (saleStatFields[`${walletId}SalesQuantity`] || 0) + 1
      }

      // Bank commission
      if (info.paymentMethod.comission && info.paymentMethod.comission > 0) {
        saleStatFields['bankPercent'] = (saleStatFields['bankPercent'] || 0) + (value * info.paymentMethod.comission / 100)
      }
    }

    if (info.secondPaymentMethod) {
      const pm2Id = info.secondPaymentMethod.id
      const value2 = info.secondPaymentMethod.value || 0
      const wallet2Id = info.secondPaymentMethod.destinationWalletForStore?.[info.store]?.id

      saleStatFields[`${pm2Id}SalesAmount`] = (saleStatFields[`${pm2Id}SalesAmount`] || 0) + value2
      saleStatFields[`${pm2Id}SalesQuantity`] = (saleStatFields[`${pm2Id}SalesQuantity`] || 0) + 1

      if (wallet2Id) {
        saleStatFields[`${wallet2Id}SalesAmount`] = (saleStatFields[`${wallet2Id}SalesAmount`] || 0) + value2
        saleStatFields[`${wallet2Id}SalesQuantity`] = (saleStatFields[`${wallet2Id}SalesQuantity`] || 0) + 1
      }

      if (info.secondPaymentMethod.comission && info.secondPaymentMethod.comission > 0) {
        saleStatFields['bankPercent'] = (saleStatFields['bankPercent'] || 0) + (value2 * info.secondPaymentMethod.comission / 100)
      }
    }

    // Purchase method
    if (info.purchaseMethod) {
      saleStatFields[`${info.purchaseMethod.id}SalesAmount`] = (saleStatFields[`${info.purchaseMethod.id}SalesAmount`] || 0) + (info.finalTotalCartPrice || 0)
      saleStatFields[`${info.purchaseMethod.id}SalesQuantity`] = (saleStatFields[`${info.purchaseMethod.id}SalesQuantity`] || 0) + 1
    }

    // By cashier
    if (info.cashierUID) {
      saleStatFields[`${info.cashierUID}SalesAmount`] = (saleStatFields[`${info.cashierUID}SalesAmount`] || 0) + (info.finalTotalCartPrice || 0)
      saleStatFields[`${info.cashierUID}SalesQuantity`] = (saleStatFields[`${info.cashierUID}SalesQuantity`] || 0) + 1
    }
  }

  // Apply negative if needed (for deletions)
  if (negative) {
    for (const key in saleStatFields) {
      saleStatFields[key] = -saleStatFields[key]
    }
  }

  // Convert to fieldValue.increment if for Firebase
  if (forFirebase) {
    const result: DailyStatIncrements = {}
    for (const key in saleStatFields) {
      result[key] = fieldValue.increment(saleStatFields[key])
    }
    return result
  }

  return saleStatFields
}

/**
 * Calculate daily stat increments for returns
 */
export const dailyStatIncrementsForReturns = (
  sales: Sale[],
  forFirebase: boolean,
  negative: boolean
): DailyStatIncrements => {
  const returnStatFields: Record<string, number> = {}

  // Filter sales that have returnInfo
  const salesWithReturns = sales.filter(sale => sale.returnInfo)

  for (const sale of salesWithReturns) {
    const returnInfo = sale.returnInfo!

    // Basic return stats
    returnStatFields['returnsAmount'] = (returnStatFields['returnsAmount'] || 0) + (returnInfo.totalReturnedAmount || 0)
    returnStatFields['returnsQuantity'] = (returnStatFields['returnsQuantity'] || 0) + 1

    // Employee-specific returns
    if (sale.cashierUID) {
      returnStatFields[`${sale.cashierUID}ReturnsAmount`] = (returnStatFields[`${sale.cashierUID}ReturnsAmount`] || 0) + (returnInfo.totalReturnedAmount || 0)
      returnStatFields[`${sale.cashierUID}ReturnsQuantity`] = (returnStatFields[`${sale.cashierUID}ReturnsQuantity`] || 0) + 1
    }

    // Payment method and wallet stats (only for money returns)
    if (returnInfo.returnType === 1 && returnInfo.returnMethod) {
      const rmId = returnInfo.returnMethod.id
      returnStatFields[`${rmId}ReturnsAmount`] = (returnStatFields[`${rmId}ReturnsAmount`] || 0) + (returnInfo.totalReturnedAmount || 0)
      returnStatFields[`${rmId}ReturnsQuantity`] = (returnStatFields[`${rmId}ReturnsQuantity`] || 0) + 1

      const walletId = returnInfo.returnMethod.destinationWalletForStore?.[sale.store]?.id
      if (walletId) {
        returnStatFields[`${walletId}ReturnsAmount`] = (returnStatFields[`${walletId}ReturnsAmount`] || 0) + (returnInfo.totalReturnedAmount || 0)
        returnStatFields[`${walletId}ReturnsQuantity`] = (returnStatFields[`${walletId}ReturnsQuantity`] || 0) + 1
      }
    }
  }

  // Apply negative flag if needed
  if (negative) {
    for (const key in returnStatFields) {
      returnStatFields[key] = -returnStatFields[key]
    }
  }

  // Apply Firebase fieldValue.increment if needed
  if (forFirebase) {
    const result: DailyStatIncrements = {}
    for (const key in returnStatFields) {
      result[key] = fieldValue.increment(returnStatFields[key])
    }
    return result
  }

  return returnStatFields
}

// ============================================================================
// Query Functions
// ============================================================================

/**
 * Get sales within a date range
 * @param db - Database reference (organization-scoped)
 * @param range - Date range to query
 * @param flat - If true, returns flat array; otherwise groups by date
 * @param store - Optional store filter
 * @param clientID - Optional client filter
 * @param withClients - If true, only returns sales with bonusAction 0 or 1
 */
export const getSalesOnDateRange = async (
  db: unknown,
  range: DateRange,
  flat = false,
  store?: string,
  clientID: string | null = null,
  withClients = false
): Promise<Sale[] | SalesByDate[]> => {
  if (shouldUseSupabase()) {
    try {
      const { organizationId } = getDbContext()

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      const params: Omit<Parameters<typeof salesApi.getSalesWindow>[0], 'idFrom' | 'idTo'> = {
        startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate as string,
        endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate as string,
      }

      if (store) params.storeId = store
      if (clientID) params.clientId = clientID

      // ATL-1477: the WHOLE range, provably. GET /sales is paginated (default 50, newest first)
      // and this used to send no limit, so a busy day came back as its last 50 sales — and the
      // product report / daily-stats validation WROTE figures computed from that back to Firestore.
      const rawSales = await readWholeList(
        (window) => salesApi.getSalesWindow({ ...params, ...window }),
        { label: 'sales', order: newestFirst('saleDate') },
      )
      const sales = rawSales
        .map(sale => transformApiItem(sale as unknown as Record<string, unknown>, 'sales'))
        .filter(Boolean) as unknown as Sale[]

      // Filter by bonusAction if withClients is true
      let filteredSales = sales
      if (withClients) {
        filteredSales = sales.filter(sale => sale.bonusAction === 0 || sale.bonusAction === 1)
      }

      if (flat) {
        return filteredSales
      } else {
        return groupSalesByDate(filteredSales, store)
      }
    } catch (error) {
      console.error('Error fetching sales from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'sales' },
        extra: { range, store, clientID, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation (fallback)
  const firestoreDb = db as FirestoreDb
  let query = firestoreDb.collection('sales') as FirestoreQuery<Sale>
  if (store) {
    query = query.where('store', '==', store)
  }
  if (clientID) {
    query = query.where('clientId', '==', clientID)
  }
  if (withClients) {
    query = query.where('bonusAction', 'in', [0, 1])
  }
  const querySnapshot = await query
    .where('saleDate', '>=', range.startDate)
    .where('saleDate', '<=', range.endDate)
    .orderBy('saleDate', 'desc')
    .get()

  if (flat) {
    const docs: Sale[] = []
    querySnapshot.forEach((doc) => {
      const snap = doc.data()
      if (!snap) return
      snap.id = doc.id
      snap.ref = doc.ref as unknown as MockRef
      docs.push(snap)
    })
    return docs
  } else {
    const grouped: Record<string, Sale[]> = {}
    querySnapshot.forEach((doc) => {
      const snap = doc.data()
      if (!snap) return
      snap.id = doc.id
      snap.ref = doc.ref as unknown as MockRef
      const saleDate = (snap.saleDate as TimestampLike).toDate()
      const dateKey = format(getLocalStoreDate(snap.store, saleDate), 'd/M/y EEEE')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(snap)
    })
    return Object.entries(grouped).map(([title, data]) => ({ title, data }))
  }
}

// ----- Shared client-aggregation helpers (backend-agnostic) -----
// Both read paths fetch the client's sales then aggregate identically, so the aggregation lives
// here and each backend only supplies the `Sale[]`.

/** Fetch up to `limit` of a client's sales from Firestore (launch read path). */
/**
 * Every sale of one client, from Firestore. ATL-1473: this used to stop at 1,000 (`limit(1000)`),
 * so a client with more had totals and top products computed over the first 1,000 Firestore returned
 * — in no stated order — while the date range (a separate count + orderBy) covered all of them.
 */
const getClientSalesFromFirestore = async (
  db: unknown,
  clientID: string
): Promise<Sale[]> => {
  const firestoreDb = db as FirestoreDb
  const querySnapshot = await (firestoreDb.collection('sales') as FirestoreQuery<Sale>)
    .where('clientId', '==', clientID)
    .get()
  const sales: Sale[] = []
  querySnapshot.forEach((doc) => {
    const snap = doc.data()
    if (!snap) return
    snap.id = doc.id
    snap.ref = doc.ref as unknown as MockRef
    sales.push(snap)
  })
  return sales
}

/** Aggregate a client's sales into summary statistics. */
const aggregateClientStatistics = (sales: Sale[]): ClientStatistics => {
  const salesCount = sales.length
  const totalRevenue = sales.reduce((sum, sale) => sum + (sale.finalTotalCartPrice || 0), 0)
  const totalCost = sales.reduce((sum, sale) => sum + (sale.totalPurchasePrice || 0), 0)
  return {
    salesCount,
    totalRevenue,
    totalCost,
    totalProfit: totalRevenue - totalCost,
  }
}

/** Aggregate a client's sales into ranked top-products. */
const aggregateClientTopProducts = (sales: Sale[], limit: number): ProductStats[] => {
  if (sales.length === 0) {
    return []
  }

  const products: Record<string, ProductStats> = {}

  sales.forEach(sale => {
    if (sale.cartItems && Array.isArray(sale.cartItems)) {
      sale.cartItems.forEach((cartItem) => {
        const productId = cartItem.id
        // Skip custom/delivery pseudo lines. Detect via lineType (Supabase-read items carry
        // a unique id + lineType) with the legacy sentinel-id fallback — a raw `id === 'customProduct'`
        // check would let new-format pseudo lines through as phantom top-products.
        if (!productId || isPseudoLine(cartItem)) return

        if (!products[productId]) {
          products[productId] = {
            name: cartItem.name || '',
            categoryID: cartItem.categoryID,
            supplierID: cartItem.supplierID,
            saleQuantity: 0,
            saleAmount: 0,
            checkQuantity: 0,
            purchaseAmount: 0,
            packageAmount: 0,
          }
        }

        const quantity = cartItem.quantity || 0
        // ATL-928: `saleAmount` is a LINE TOTAL, and `cartItem.price` is a UNIT price that exists
        // only on the Supabase read path — so `+= price` used to mean the charged line total when
        // reading Firestore and one unit at the list price when reading Supabase. See
        // cartLineAmount.ts; this makes the number the owner sees survive the cutover unchanged.
        const lineAmount = chargedLineAmount(cartItem)
        const purchasePrice = cartItem.purchasePrice || 0
        const packagePrice = cartItem.packagePrice || 0

        products[productId].saleQuantity += quantity
        products[productId].saleAmount += lineAmount
        products[productId].checkQuantity += 1
        products[productId].purchaseAmount += quantity * purchasePrice
        products[productId].packageAmount += quantity * packagePrice
      })
    }
  })

  const productArray = Object.values(products).map(product => {
    product.margin = (product.saleAmount || 0) - (product.purchaseAmount || 0) - (product.packageAmount || 0)
    product.profit = (product.margin || 0) - (product.writeOffAmount || 0)
    return product
  })

  productArray.sort((a, b) => b.saleQuantity - a.saleQuantity)
  return productArray.slice(0, limit)
}

/** Everything the client card shows about a client's purchases, from ONE read of their sales. */
export interface ClientSalesSummary {
  statistics: ClientStatistics
  topProducts: ProductStats[]
  earliestDate: Date | null
  latestDate: Date | null
}

/** Summarize a client's sales: totals, top products, and the first and last purchase dates. */
export const summarizeClientSales = (sales: Sale[], topProductsLimit: number): ClientSalesSummary => {
  let earliestDate: Date | null = null
  let latestDate: Date | null = null
  for (const sale of sales) {
    const date = toDate(sale.saleDate as unknown as Parameters<typeof toDate>[0])
    if (!date || Number.isNaN(date.getTime())) continue
    if (!earliestDate || date < earliestDate) earliestDate = date
    if (!latestDate || date > latestDate) latestDate = date
  }
  return {
    statistics: aggregateClientStatistics(sales),
    topProducts: aggregateClientTopProducts(sales, topProductsLimit),
    earliestDate,
    latestDate,
  }
}

/**
 * ATL-1473 — the client card's purchase summary from ONE read of the client's sales.
 *
 * The card used to call getClientStatisticsAggregated, getClientTopProductsAggregated and
 * getClientSalesDateRange side by side, and each read the client's WHOLE sales list on its own: the
 * same list fetched, hydrated and validated three times per card open. All three were already
 * aggregated in the app from that list, so reading it once changes no number. Both read modes read
 * every sale (the Firestore path used to stop at 1,000 — see getClientSalesFromFirestore).
 */
export const getClientSalesSummary = async (
  db: unknown,
  clientID: string,
  topProductsLimit = 15
): Promise<ClientSalesSummary> => {
  if (shouldUseSupabase()) {
    try {
      const rawSales = await readWholeList(
        (window) => salesApi.getSalesWindow({ clientId: clientID, ...window }),
        { label: 'sales', order: newestFirst('saleDate') },
      )
      const sales = rawSales
        .map(sale => transformApiItem(sale as unknown as Record<string, unknown>, 'sales'))
        .filter(Boolean) as unknown as Sale[]
      return summarizeClientSales(sales, topProductsLimit)
    } catch (error) {
      console.error('Error fetching client sales summary from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'sales', function: 'getClientSalesSummary' },
        extra: { clientID, errorMessage: (error as Error).message },
      })
      throw error
    }
  }
  return summarizeClientSales(await getClientSalesFromFirestore(db, clientID), topProductsLimit)
}

/**
 * Get client statistics aggregated from sales
 */
export const getClientStatisticsAggregated = async (
  db: unknown,
  clientID: string
): Promise<ClientStatistics> => {
  if (shouldUseSupabase()) {
    try {
      // ATL-1477: every sale of the client. A single `limit: 1000` request silently aggregated
      // only the newest 1,000 for a client with more.
      const rawSales = await readWholeList(
        (window) => salesApi.getSalesWindow({ clientId: clientID, ...window }),
        { label: 'sales', order: newestFirst('saleDate') },
      )
      const sales = rawSales
        .map(sale => transformApiItem(sale as unknown as Record<string, unknown>, 'sales'))
        .filter(Boolean) as unknown as Sale[]

      return aggregateClientStatistics(sales)
    } catch (error) {
      console.error('Error fetching client statistics from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'sales', function: 'getClientStatisticsAggregated' },
        extra: { clientID, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore read path (launch default): query the client's sales and aggregate in-process.
  const sales = await getClientSalesFromFirestore(db, clientID)
  return aggregateClientStatistics(sales)
}

/**
 * Get top products purchased by a client
 */
export const getClientTopProductsAggregated = async (
  db: unknown,
  clientID: string,
  limit = 10
): Promise<ProductStats[]> => {
  if (shouldUseSupabase()) {
    try {
      // ATL-1477: every sale of the client. A single `limit: 1000` request silently aggregated
      // only the newest 1,000 for a client with more.
      const rawSales = await readWholeList(
        (window) => salesApi.getSalesWindow({ clientId: clientID, ...window }),
        { label: 'sales', order: newestFirst('saleDate') },
      )
      const sales = rawSales
        .map(sale => transformApiItem(sale as unknown as Record<string, unknown>, 'sales'))
        .filter(Boolean) as unknown as Sale[]

      return aggregateClientTopProducts(sales, limit)
    } catch (error) {
      console.error('Error fetching client top products from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'sales', function: 'getClientTopProductsAggregated' },
        extra: { clientID, limit, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore read path (launch default): query the client's sales and aggregate in-process.
  const sales = await getClientSalesFromFirestore(db, clientID)
  return aggregateClientTopProducts(sales, limit)
}

// ============================================================================
// Write Operations
// ============================================================================

// Note: Write operations use 'any' types due to complex Firestore batch operations
// and deep integration with untyped modules. Full typing would require extensive refactoring.

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface SaveNewSaleOptions {
  /**
   * ATL-1644 — called once with the terminal path of this sale's Supabase dual-write, after the
   * Firestore commit and before `saveNewSale` resolves. Not called when `saveNewSale` throws (the
   * sale's analytics are not emitted on that path either — see ATL-1320). Every path on which
   * `bestEffortDualWrite` resolves reports an outcome, so a resolved save always calls this; a
   * caller that nevertheless sees none must record it as unknown (a defect signal), never guess.
   * The sale is already committed when this runs, so a throw from it is reported to Sentry and
   * swallowed: an analytics observer must never turn a saved sale into a checkout error.
   *
   * ATL-1445 stage 3 P9, Supabase mode (no dual-write): called once with `{ kind: 'landed' }` when the
   * server acknowledged the sale — the only way that mode resolves — and never when the write rejects
   * (held, discarded, not sent, refused at plan time). See `reportSupabaseModeSaleLanded`.
   */
  onDualWriteOutcome?: (outcome: DualWriteOutcome) => void
}

/**
 * Save a new sale with stock updates, transactions, and client bonus handling.
 *
 * ATL-1445 stage 3 P9 — through the `writeW` seam. In Firestore mode (every session today) the seam runs
 * `firestoreFirst`, which is master's body VERBATIM: same batch, same order, same mirror intent (P7),
 * same dual-write, same errors and the same microtasks (`salesWriters.p9Differential.test.js`). In
 * Supabase mode (unreachable while any W writer is unconverted) the seam runs `plan` instead and puts
 * the sale in outbox v2 (`writeWBackendFirst.ts`): the request is the one this body's dual-write sends
 * for the same input, built from the same `buildSaleCreateDto`; the local effects are `saleLocalEffects`
 * (the batch's own stock loop and increments). Differences in Supabase mode, each deliberate:
 *  - `setTimezone(info.store)` is not called: it seeds a missing store timezone through
 *    `updateStoreTimezone`, a `stores` writer that is not converted yet (P13); calling it would be a
 *    second master of the store document. The timezone stays unset until P13 (a store's first sale is
 *    the only one that seeds it).
 *  - A NaN quantity or total is refused at plan time (P6 open question 11, the doc's recommendation):
 *    Firestore mode still books it, as today.
 *  - A DTO the API's own request validation refuses (`RequestNotCapturedError`) refuses the sale: today
 *    such a sale is booked in Firestore and missing from Supabase for good (stage-0 defect 6).
 *  - Pay later (no payment method), ATL-1542, AWAITING BEKS: the backend's `create_sale_atomic` applies
 *    the client's bonus, punch card and visit count when the UNPAID sale is created, and
 *    `POST /sales/:id/payment` applies none; Firestore mode applies them at payment. P9 changes neither
 *    side (§17 "ATL-1542").
 */
export const saveNewSale = (db: any, info: any, options: SaveNewSaleOptions = {}): Promise<MockRef> =>
  writeW({
    label: 'sales.create',
    kind: 'create',
    offline: 'queue',
    firestoreFirst: async () => {
      const batch = rootDb.batch()
      const jotaiStore = getDefaultStore()
      const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Prepared for future use
      const _storesDict = jotaiStore.get(storesDictAtom) as Record<string, any>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Prepared for future use
      const _constants = jotaiStore.get(constantsAtom) as any

      // create ref for new sale
      const ref = db.collection('sales').doc()
      const transactionRef = db.collection('transactions').doc()

      // update inStock of product
      //
      // ATL-1381: `previousInStock` comes from a tracker that advances per line, NOT from a fresh
      // read of the dict on every line. The dict is a listener-fed read model and nothing updates it
      // while this batch is being built, so two lines drawing on the same product used to record the
      // SAME baseline while each applied its own increment — breaking the movement chain by the first
      // line's quantity and spending the merchant-facing «возможно нужен пересчет» alarm on a sale
      // where nothing went wrong. Two lines for one product is ordinary input: `mergeSimilarItems`
      // off makes every tap its own line, and two composite lines routinely share an ingredient.
      // See stockBaseline.ts for what this does NOT fix (the at-least-once double-apply — guarded at the
      // commit below by a write claim, ATL-2004).
      const storeStockField = 'storeStock.' + info.store
      const stockBaseline = stockBaselineTracker(allProductsDict, info.store)
      // ATL-1445 stage 3 P6: WHICH product takes WHAT increment is decided by `saleStockLines` — master's
      // loop with its two writes taken out — so the local view's `saleStockEffects` and this batch are
      // the same computation. It is a generator: each line's validation and diagnostics still run here,
      // between the previous line's writes and this one's, exactly as before (saleStockEffects.ts).
      for (const line of saleStockLines(info, allProductsDict)) {
        // Get real Firestore ref (handles both legacy refs and Supabase mock refs)
        const lineRef = getFirestoreRef(line.refSource)
        const lineChain = stockBaseline.next(line.productId, line.increment)
        batch.update(lineRef, { [storeStockField]: fieldValue.increment(line.increment) })
        batch.set(lineRef.collection('movement').doc(), {
          productID: line.productId,
          type: 'sale',
          previousInStock: lineChain.previousInStock,
          writeSeq: lineChain.writeSeq,
          increment: line.increment,
          date: fieldValue.serverTimestamp(),
          linkedDocId: ref.id,
          linkedDocDate: info.saleDate,
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId,
        })
      }

      // create sale cart items
      const newCartItems: any[] = []
      for (const value of info.cartItems) {
        const cartItemRef = ref.collection('cartItems').doc()
        // P9: the line document is built by `saleCartItemDocument`, which the Supabase-mode plan uses too.
        const updatedCartItem = saleCartItemDocument(value, cartItemRef, ref.id, info)
        batch.set(cartItemRef, updatedCartItem)
        newCartItems.push(updatedCartItem)
      }

      // save new sale with updated cart items
      // Sanitize info to remove non-serializable values (mock refs, mock timestamps)
      const sanitizedInfo = sanitizeForFirestore(info)
      const updatedInfo: any = {
        ...sanitizedInfo,
        cartItems: newCartItems,
      }
      if (info.paymentMethod) {
        updatedInfo.transactionId = transactionRef.id
      }

      // update bonuses for client
      if (info.client != null && info.paymentMethod) {
        const clientUpdateFields: any = {
          totalPurchasedValue: fieldValue.increment(info.finalTotalCartPrice),
          lastPurchaseDate: info.saleDate,
          lastPurchaseSource: info.store,
        }
        if (info.cashbackEnabled) {
          clientUpdateFields.bonusValue = fieldValue.increment(info.bonusAccumulation - info.bonusReduction)
        }
        if (info.punchCardEnabled) {
          let punchCardValueAccumulation = 0
          newCartItems.forEach((item) => {
            if (item.punchCardEnabled) {
              punchCardValueAccumulation += item.quantity
            }
          })
          if (info.punchCardReduction > 0) {
            punchCardValueAccumulation = punchCardValueAccumulation - info.punchCardCapacity
          }
          clientUpdateFields.punchCardValue = fieldValue.increment(punchCardValueAccumulation)
        }
        batch.update(db.collection('clients').doc(info.client.id), clientUpdateFields)
      }

      // payment methods, wallets and save payment transaction
      if (info.paymentMethod) {
        const walletId = info.paymentMethod.destinationWalletForStore?.[info.store]?.id
        batch.set(transactionRef, {
          linkedDocId: ref.id,
          linkedDocType: 'sale',
          type: 'income',
          destinationWallet: walletId || '',
          value: info.paymentMethod.value || info.finalTotalCartPrice,
          date: info.saleDate || new Date(),
          store: info.store,
          organizationId: info.organizationId,
          createdDate: new Date(),
        })
      }
      // ATL-821: hoisted out of the block below so the dual-write payload can carry it. The backend
      // stamps it onto the Supabase split-payment income row as its firestore_id; without it that row
      // is unaddressable and the reconciler copies this Firestore doc in as a duplicate.
      let secondTransactionId: string | undefined
      if (info.secondPaymentMethod) {
        const secondWalletId = info.secondPaymentMethod.destinationWalletForStore?.[info.store]?.id
        const secondTransactionRef = db.collection('transactions').doc()
        secondTransactionId = secondTransactionRef.id
        batch.set(secondTransactionRef, {
          linkedDocId: ref.id,
          linkedDocType: 'sale',
          type: 'income',
          destinationWallet: secondWalletId || '',
          value: info.secondPaymentMethod.value,
          date: info.saleDate || new Date(),
          store: info.store,
          organizationId: info.organizationId,
          createdDate: new Date(),
        })
        updatedInfo.secondTransactionId = secondTransactionRef.id
      }

      // update dailyStats
      const saleStatFields = dailyStatIncrementsForSales([info], true, false)
      batch.set(
        db.collection('dailyStats').doc(info.dailyStatID),
        {
          ...saleStatFields,
          store: info.store,
          date: info.saleDate || new Date(),
          organizationId: info.organizationId,
        },
        { merge: true }
      )

      batch.set(ref, updatedInfo)

      // ATL-1459 — when this org has mirror intents on (`isMirrorIntentActive`: the Remote Config kill
      // switch and the session's circuit breaker, answered synchronously), the mirror's DTO is built BEFORE
      // the commit (it reads only what the batch was built from, and every step copies), so its request can
      // be persisted as an intent first: an app killed between the commit and the mirror below no longer
      // loses the sale from Supabase (see mirrorIntent.ts). A DTO that cannot be built is thrown exactly
      // where it was thrown before — after the commit and `setTimezone` — so a sale is never refused by the
      // intent machinery. OFF is today's code path exactly: commit, then build the DTO, nothing awaited.
      const intentOrg = typeof db?.id === 'string' ? (db.id as string) : undefined
      const intentOn = isMirrorIntentActive(intentOrg)
      let earlyDto: Record<string, unknown> | undefined
      let earlyDtoError: unknown
      if (intentOn) {
        try {
          earlyDto = buildSaleCreateDto(ref.id, info, sanitizedInfo, newCartItems, transactionRef.id, secondTransactionId)
        } catch (error) {
          earlyDtoError = error
        }
      }
      const intentDto = earlyDto
      const mirrorIntent = intentDto
        ? await persistMirrorIntent({
            label: 'sales.create',
            documentId: ref.id,
            witness: { collection: 'sales' },
            orgFirestoreId: intentOrg,
            capture: () =>
              captureWriteRequest((client) =>
                createSalesApi(client).createSale(intentDto as Parameters<typeof salesApi.createSale>[0]),
              ),
          })
        : null

      try {
        // ATL-2004: a re-delivery of this batch (lost ack → the SDK re-sends it) must not apply its
        // increments again. With a claim, the re-delivery is refused atomically and this resolves
        // once the server confirms the sale exists — so the dual-write below still runs exactly once.
        // An outcome that cannot be established throws `WriteOutcomeUnknownError`: the intent is
        // abandoned to recovery, whose Firestore witness decides (never guessed here).
        await commitWithRedeliveryGuard(writeClaimGuard(), {
          writer: 'sales.create',
          orgFirestoreId: intentOrg,
          primaryRef: ref,
          batch,
          serverTimestamp: () => fieldValue.serverTimestamp(),
        })
      } catch (error) {
        mirrorIntent?.abandon()
        throw error
      }

      setTimezone(info.store)

      if (intentOn && !intentDto) throw earlyDtoError
      const dto = intentDto ?? buildSaleCreateDto(ref.id, info, sanitizedInfo, newCartItems, transactionRef.id, secondTransactionId)
      let dualWriteOutcome: DualWriteOutcome | undefined

      try {
        await bestEffortDualWrite(
          'sales.create',
          () => salesApi.createSale(dto as Parameters<typeof salesApi.createSale>[0]),
          undefined,
          (outcome) => {
            dualWriteOutcome = outcome
          },
        )
      } catch (error) {
        mirrorIntent?.abandon()
        throw error
      }
      mirrorIntent?.release()
      if (dualWriteOutcome && options.onDualWriteOutcome) {
        try {
          options.onDualWriteOutcome(dualWriteOutcome)
        } catch (error) {
          Sentry.captureException(error, {
            level: 'error',
            tags: { sale_dual_write_outcome_callback_threw: true, dual_write_outcome: dualWriteOutcome.kind },
            fingerprint: ['save-new-sale-dual-write-outcome-callback-threw'],
            contexts: { sale_dual_write_outcome: { saleId: ref.id, outcome: dualWriteOutcome } },
          })
        }
      }

      return ref
    },
    // Supabase mode only (P9; `writeW.ts` decision 1). Mints the same ids in the same per-collection
    // order as the body above, validates as the batch's loop does, and CAPTURES the request the body's
    // dual-write sends — nothing here writes Firestore or sends a request.
    plan: async () => {
      const allProductsDict = getDefaultStore().get(productsDictAtom) as Record<string, any>
      const ref = db.collection('sales').doc()
      const transactionRef = db.collection('transactions').doc()
      // The batch's own loop (`INGREDIENT_NOT_FOUND` where the batch throws it), its own increments, and
      // the plan-time refusal of effects the local view could not read back (a NaN quantity).
      const effects = saleLocalEffects(ref.id, info, allProductsDict)
      const sanitizedInfo = sanitizeForFirestore(info)
      const newCartItems = (info.cartItems as any[]).map((value: any) =>
        saleCartItemDocument(value, ref.collection('cartItems').doc(), ref.id, info))
      const secondTransactionId: string | undefined = info.secondPaymentMethod ? db.collection('transactions').doc().id : undefined
      const dto = buildSaleCreateDto(ref.id, info, sanitizedInfo, newCartItems, transactionRef.id, secondTransactionId)
      const request = await buildOutboxRequest((client) =>
        createSalesApi(client).createSale(dto as Parameters<typeof salesApi.createSale>[0]))
      return {
        documentId: ref.id,
        relatedIds: saleRelatedIds(info),
        request,
        effects,
        fromAck: () => {
          reportSupabaseModeSaleLanded(options, ref.id)
          return ref
        },
      }
    },
  })

/**
 * ATL-1644 × ATL-1445 stage 3 P9 — `onDualWriteOutcome` in Supabase mode. There is no dual-write there:
 * the sale IS the backend write, and `saveNewSale` resolves only through `fromAck`, i.e. once the server
 * acknowledged it (`writeWBackendFirst.ts` `queued`: a held, discarded or unsent write rejects and never
 * reaches `fromAck`). So the question the outcome answers — did this sale reach Supabase — is `landed`,
 * reported exactly once per resolution. Reporting nothing would make the caller record `unknown`, which
 * ATL-1644 defines as a defect signal. A throwing callback is reported under the Firestore-mode body's own
 * fingerprint and swallowed, as there: the sale is booked, and an observer must not turn it into an error.
 */
function reportSupabaseModeSaleLanded(options: SaveNewSaleOptions, saleId: string): void {
  if (!options.onDualWriteOutcome) return
  const outcome: DualWriteOutcome = { kind: 'landed' }
  try {
    options.onDualWriteOutcome(outcome)
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: { sale_dual_write_outcome_callback_threw: true, dual_write_outcome: outcome.kind },
      fingerprint: ['save-new-sale-dual-write-outcome-callback-threw'],
      contexts: { sale_dual_write_outcome: { saleId, outcome } },
    })
  }
}

/**
 * One cart line's `cartItems` document as `saveNewSale` writes it (and its Supabase-mode plan sends it).
 * Sanitized to remove non-serializable values (mock refs, mock timestamps); then the line's own id, its
 * reference (Firestore only: the request builder sanitizes it away), the sale and the client.
 */
const saleCartItemDocument = (value: any, cartItemRef: any, saleId: string, info: any): any => {
  // Sanitize cart item to remove non-serializable values (mock refs, mock timestamps)
  const sanitizedValue = sanitizeForFirestore(value)
  return {
    ...sanitizedValue,
    cartItemId: cartItemRef.id,
    cartItemRef: cartItemRef,
    saleID: saleId,
    store: info.store,
    saleDate: info.saleDate,
    clientId: info.clientId || null,
    clientPhone: info.clientPhone || null,
  }
}

/**
 * The documents a sale's request references (`WPlan.relatedIds`): its client and every product its lines
 * name, ingredients included. A queued delete of one of them is then never allowed to purge that
 * document's queued create ahead of this sale (`planDeletePurge`'s `referenced` rule).
 */
const saleRelatedIds = (info: any): string[] => {
  const ids = new Set<string>()
  if (typeof info.client?.id === 'string' && info.client.id.length > 0) ids.add(info.client.id)
  for (const line of info.cartItems ?? []) {
    if (!line || isPseudoLine(line)) continue
    if (typeof line.id === 'string' && line.id.length > 0) ids.add(line.id)
    if (line.isComposite) {
      for (const ingredient of line.ingredients ?? []) {
        if (typeof ingredient?.id === 'string' && ingredient.id.length > 0) ids.add(ingredient.id)
      }
    }
  }
  return [...ids]
}

/**
 * ATL-1445 stage 3 P6 — the LOCAL EFFECTS of `saveNewSale(db, info)` for the sale `saleId`: what the
 * device shows for it while it is an unsent master write (plan §3; `global/Write/localView.ts`).
 * Built from the same code as the Firestore batch above, so the two cannot drift:
 *  - stock: `saleStockEffects` — the batch's own line loop (`saleStockLines`), composites expanded,
 *    pseudo lines skipped, `INGREDIENT_NOT_FOUND` thrown where the batch throws it;
 *  - today's totals: the batch's own sale-increment function with `forFirebase = false`, i.e. the same
 *    numbers the batch wraps in `fieldValue.increment` — booked to `info.dailyStatID`, the day the
 *    batch writes (invariant 7);
 *  - the sale's row in the operations list: the sanitized sale with its lines slimmed as the request's
 *    are (display JSON: dates as ISO instants).
 * Pure; reads nothing but its arguments. P9's `planSaveNewSale` puts it on the queued entry. A
 * `dailyStatID` that is not a `d-M-y-<store>` id contributes no totals rather than a guessed day.
 * Throws `LocalEffectsInvalidError` for effects the local view could not read back (a non-finite
 * quantity or total) — at plan time, never a sale that silently shows as nothing.
 */
export const saleLocalEffects = (saleId: string, info: any, allProductsDict: Record<string, any>): LocalEffects => {
  const stock = saleStockEffects(info, allProductsDict)
  const dailyStats: DailyStatEffect[] = []
  const businessDay = businessDayOfDailyStatId(info.dailyStatID)
  if (businessDay !== null && typeof info.store === 'string' && info.store.length > 0) {
    dailyStats.push({
      storeId: info.store,
      dailyStatId: info.dailyStatID,
      businessDay,
      increments: rawIncrements(dailyStatIncrementsForSales([info], false, false)),
      assign: {},
    })
  }
  const sanitized = sanitizeForFirestore(info) as Record<string, unknown>
  const cartItems = Array.isArray(sanitized.cartItems) ? (sanitized.cartItems as unknown[]).map(stripHeavyProductCartFields) : []
  return assertLocalEffects({
    v: LOCAL_EFFECTS_VERSION,
    stock,
    dailyStats,
    records: [
      {
        collection: 'sales',
        id: saleId,
        op: 'create',
        store: typeof info.store === 'string' && info.store.length > 0 ? info.store : null,
        date: isoInstantOf(info.saleDate),
        fields: toDisplayObject({ ...sanitized, cartItems, id: saleId, firestoreId: saleId }),
      },
    ],
  })
}

/**
 * The `POST /sales` mirror of a sale `saveNewSale` has just built — pass-through in Firestore format.
 * Backend accepts Firestore field names (store, dailyStatID, paymentMethod as object, etc.) and handles
 * transformation to Supabase format internally. Pure: every step copies, nothing it reads is mutated.
 */
const buildSaleCreateDto = (
  saleId: string,
  info: any,
  sanitizedInfo: unknown,
  newCartItems: any[],
  transactionId: string,
  secondTransactionId: string | undefined,
): Record<string, unknown> => {
  // Pass-through cart items in Firestore format — backend normalizes prices (Principle #2).
  // Strip heavy product-catalog fields the backend doesn't store, to keep the body small (ATL-608).
  const cartItemsForApi = newCartItems.map((item: any) =>
    coerceCartItemForApi(stripHeavyProductCartFields(sanitizeForFirestore(item))))

  // Pass-through: send the same data format as Firestore
  // Backend DTO accepts Firestore field names: store, dailyStatID, paymentMethod (object), etc.
  const saleInfo = { ...(sanitizedInfo as Record<string, unknown>) }
  delete (saleInfo as { organizationId?: unknown }).organizationId
  delete (saleInfo as { createdDate?: unknown }).createdDate
  return filterUndefined({
    firestoreId: saleId,
    ...saleInfo,
    cartItems: cartItemsForApi,
    // ATL-821: only send the transaction doc ids that were actually WRITTEN above. The backend
    // stamps them as the Supabase transactions' firestore_id, so sending an id for a doc that
    // does not exist would make the Supabase row point at nothing — worse than leaving it NULL.
    // `transactionRef` is minted unconditionally but only committed when info.paymentMethod is
    // set, which is the same condition create_sale_atomic uses to decide whether to mint a row.
    transactionId: info.paymentMethod ? transactionId : undefined,
    secondTransactionId,
    // Convert saleDate to ISO string if it's a Date
    saleDate: info.saleDate?.toDate
      ? info.saleDate.toDate().toISOString()
      : info.saleDate instanceof Date
        ? info.saleDate.toISOString()
        : info.saleDate,
  } as Record<string, unknown>)
}

/**
 * Update an existing sale.
 *
 * ATL-1445 stage 3 P9 — through the `writeW` seam: Firestore mode runs master's body verbatim
 * (`firestoreFirst`); Supabase mode queues the PATCH this body's dual-write sends (`buildSaleUpdateDto`,
 * the same function), with the sale row's patch as its local effect.
 */
export const updateSale = (refOrItem: MockRef | Record<string, unknown>, info: any): Promise<void> =>
  writeW({
    label: 'sales.update',
    kind: 'update',
    offline: 'queue',
    firestoreFirst: async () => {
      // Resolve to a real Firestore ref — accepts the whole sale object (preferred; works when
      // `.ref` was stripped, ATL-548 pattern), a real ref, or a mock ref.
      const saleRef = getFirestoreRef(refOrItem, 'sales')
      // Strip undefined before the Firestore write — Firestore .update() rejects undefined field
      // values (same class as the new-sale crash, 2AC/ATL-607). filterUndefined is undefined-only
      // (preserves Timestamps, cycle-guarded) so it won't mangle the fiscalInfo payloads callers send.
      await saleRef.update(filterUndefined(info as Record<string, unknown>))

      // Dual write to Supabase — the PATCH body is built by `buildSaleUpdateDto` (shared with the plan).
      const filteredInfo = buildSaleUpdateDto(info)

      await bestEffortDualWrite('sales.update', () =>
        salesApi.updateSale(saleRef.id, filteredInfo as Parameters<typeof salesApi.updateSale>[1])
      )
    },
    plan: async () => {
      const saleRef = getFirestoreRef(refOrItem, 'sales')
      const filteredInfo = buildSaleUpdateDto(info)
      const request = await buildOutboxRequest((client) =>
        createSalesApi(client).updateSale(saleRef.id, filteredInfo as Parameters<typeof salesApi.updateSale>[1]))
      return { documentId: saleRef.id, request, effects: salePatchEffects(saleRef.id, info), fromAck: () => undefined }
    },
  })

/**
 * The `PATCH /sales/:id` body of a sale edit — pass-through in Firestore format (the backend accepts
 * Firestore field names and objects: paymentMethod, purchaseMethod, …). `updateSale`'s dual-write and
 * its Supabase-mode plan both send it.
 */
const buildSaleUpdateDto = (info: any): Record<string, unknown> => {
  // Dual write to Supabase - pass through in Firestore format
  // Backend accepts Firestore field names and objects (paymentMethod, purchaseMethod, etc.)
  const sanitizedInfo = sanitizeForFirestore(info)
  const updateInfo = { ...(sanitizedInfo as Record<string, unknown>) }
  delete (updateInfo as { organizationId?: unknown }).organizationId
  delete (updateInfo as { createdDate?: unknown }).createdDate
  const filteredInfo = filterUndefined(updateInfo as Record<string, unknown>)

  // Cart items passed through as-is — backend normalizes prices (Principle #2) — but strip the
  // heavy product-catalog fields the backend doesn't store: full product objects per line can
  // push the PATCH body past the body-parser limit → PayloadTooLargeError (ATL-608 / 29Y).
  //
  // ATL-880 — AND coerce them, exactly as `saveNewSale` does. This path was left on
  // `stripHeavyProductCartFields` alone when ATL-871 added `coerceCartItemForApi` to the create
  // path, so every shape the coercer exists to fix still killed an UPDATE: a `NaN` purchasePrice
  // inherited from one of the 3,897 product docs that carry one (fleet census 2026-08-06, 300
  // orgs), a numeric `barcode`, a Firestore Timestamp in `createdAt`. The generated Zod request
  // schema rejects those client-side, `bestEffortDualWrite` classifies a request-validation
  // rejection as a deterministic defect, and deterministic defects are never queued in the outbox —
  // so the edit (fiscal receipt data, a corrected cart, `returnInfo`) was lost from Supabase
  // permanently while Firestore had already committed it.
  const cartItems = (filteredInfo as { cartItems?: unknown }).cartItems
  if (Array.isArray(cartItems)) {
    (filteredInfo as { cartItems: unknown[] }).cartItems = cartItems.map((item) =>
      coerceCartItemForApi(stripHeavyProductCartFields(item)))
  }
  return filteredInfo
}

/**
 * The local effect of a sale edit (P9): the patched fields over the sale's row, as display JSON. The
 * local view applies a patch only to a row this device created and has not seen acknowledged (P6
 * decision 4); a server row waits for the list's next read.
 */
const salePatchEffects = (saleId: string, info: any): LocalEffects => {
  const sanitized = sanitizeForFirestore(info) as Record<string, unknown>
  const fields: Record<string, unknown> = { ...sanitized }
  if (Array.isArray(sanitized.cartItems)) fields.cartItems = (sanitized.cartItems as unknown[]).map(stripHeavyProductCartFields)
  return assertLocalEffects({
    v: LOCAL_EFFECTS_VERSION,
    stock: [],
    dailyStats: [],
    records: [
      {
        collection: 'sales',
        id: saleId,
        op: 'patch',
        store: typeof info?.store === 'string' && info.store.length > 0 ? info.store : null,
        date: isoInstantOf(info?.saleDate),
        fields: toDisplayObject(fields),
      },
    ],
  })
}

/**
 * Mirror a receipt the Firestore write master ALREADY holds onto the sale's Supabase row — the
 * Supabase half of `updateSale` only, with NO Firestore write.
 *
 * Used when a fiscal retry reads the master and finds the sale already signed
 * (utils/fiscalRetryGuard): the screen offered the retry because the row it read lacked the
 * receipt, i.e. the mirror PATCH of the original signing never landed. Re-writing Firestore would
 * change nothing there; re-sending the mirror repairs the row. It goes through the ordinary
 * `bestEffortDualWrite('sales.update', …)` — same label, gate, outbox and failure classification
 * as every other sale mirror — so an offline till queues it like any other update.
 *
 * ATL-1445 stage 3 P9 — through the `writeW` seam. Firestore mode: master's body, verbatim. Supabase
 * mode: the same PATCH, queued as a master write (the sale's row in the master store takes the receipt).
 */
export const mirrorSaleFiscalInfo = (
  refOrItem: MockRef | Record<string, unknown>,
  fiscalInfo: FiscalInfo,
): Promise<void> =>
  writeW({
    label: 'sales.update',
    kind: 'update',
    offline: 'queue',
    firestoreFirst: async () => {
      const saleRef = getFirestoreRef(refOrItem, 'sales')
      await bestEffortDualWrite('sales.update', () =>
        salesApi.updateSale(saleRef.id, { fiscalInfo } as Parameters<typeof salesApi.updateSale>[1])
      )
    },
    plan: async () => {
      const saleRef = getFirestoreRef(refOrItem, 'sales')
      const request = await buildOutboxRequest((client) =>
        createSalesApi(client).updateSale(saleRef.id, { fiscalInfo } as Parameters<typeof salesApi.updateSale>[1]))
      return { documentId: saleRef.id, request, effects: salePatchEffects(saleRef.id, { fiscalInfo }), fromAck: () => undefined }
    },
  })

/**
 * Delete a sale with stock restoration.
 *
 * ATL-1445 stage 3 P9 — through the `writeW` seam, `offline: 'refuse'` (invariant 12: a void fails
 * visibly offline and applies nothing — today it runs in a Firestore transaction, which needs the
 * server). Firestore mode runs master's body verbatim (`firestoreFirst`). Supabase mode sends the
 * `DELETE /sales/:id` this body's dual-write sends, ONCE, behind an empty queue (`writeWBackendFirst.ts`):
 * the backend's `delete_sale_atomic` reverses the stock movements, the money and the client's effects.
 * After the server's acknowledgement it runs the void's non-W tail, `deletedSaleTail`: the `deleteSale`
 * record of the Updates feed (outside W: Firestore first, then its v1 mirror), the audit log, and the
 * fiscal return — the same three this body performs, held to it by `salesWritersSupabase.p9.test.js`.
 */
export const deleteSale = (
  db: any,
  ref: MockRef,
  info: any,
  uid: string,
  fiscalOperator: any = null,
  OFDCredentials: any = null
): Promise<void> =>
  writeW({
    label: 'sales.delete',
    kind: 'delete',
    offline: 'refuse',
    firestoreFirst: async () => {
      const jotaiStore = getDefaultStore()
      const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>

      // Resolve ref to a real Firestore DocumentReference (may be a plain {id} object from Supabase)
      const saleRef = getFirestoreRef({ ...info, ref }, 'sales')

      // ATL-779 / ATL-670: this reversal runs inside runIdempotentDelete, NOT a plain rootDb.batch().
      //
      // A WriteBatch is atomic but NOT idempotent: `batch.update(ref, {'storeStock.s': increment(+q)})`
      // is a SERVER-SIDE transform that re-applies on every delivery, while `batch.delete` of an
      // already-deleted doc does not error. So a re-delivered delete restored the stock again, drove
      // `dailyStats` further negative, and credited the client's bonuses twice — the class that reached
      // −645 units at org VSH (ATL-670) and that ATL-779 confirmed still live on the sales path.
      // Every other stock-reversing delete already routes through this helper; sales were the last two.
      //
      // The transaction reads the sale doc first: gone ⇒ the reversal already ran (or the record is
      // Supabase-only), so it is skipped entirely and the caller still performs its Supabase cleanup.
      // `applyWrites` may only WRITE — Firestore requires all reads before writes, and the one
      // existence read is done inside the helper. The primary doc is deleted for us.
      //
      // Built here rather than inside the callback: `applyWrites` may run more than once if the
      // transaction retries, and the audit mirror below needs the same object after it commits.
      // ATL-911: bind the ref BEFORE building the record, so the document's generated id is
      // part of it. An anonymous `.doc()` mints an id and discards it, which is why the
      // Supabase mirror landed with firestore_id NULL and its Firestore twin read as missing.
      const deleteSaleUpdateRef = db.collection('updates').doc()
      const deleteSaleUpdate = {
        firestoreId: (deleteSaleUpdateRef as { id: string }).id,
        date: new Date(),
        cashierUID: uid,
        type: 'deleteSale',
        dataBefore: sanitizeForFirestore(info),
      }

      // ⚠️ Deliberately NOT applied to `saveNewSale`. Firestore transactions FAIL when the client is
      // offline (Firebase docs, "Updating data with transactions"), whereas a queued batch commits on
      // reconnect. `NewSaleScreen.js:836-860` clears the cart BEFORE awaiting the commit precisely so a
      // cashier can keep selling offline, so the same conversion there would turn every offline sale
      // into a visible error with the cart already cleared. See the note on ATL-779.
      // ATL-767: a dict miss is no longer a silent skip — the restore below proceeds from the
      // cart line itself. But the miss is still a signal (product deleted from the catalog, or a
      // stale/partial dict), so it is REPORTED instead of swallowed. Computed OUTSIDE the
      // transaction callback (which may retry) so the event fires exactly once per delete.
      // Review defect 1: the guard must be `isPseudoLine`, not the raw sentinels — a sale read
      // back from Supabase carries pseudo lines whose `id` was replaced by the unique cartItemId
      // (tagPseudoLineForApi), and the codebase documents that exact trap at the getSales
      // transform. A sentinel-only check would treat those lines as products and abort the
      // whole delete transaction on their nonexistent refs.
      // Each miss keeps its SOURCE OBJECT: the existence pre-check must resolve its ref with the
      // LOOP'S OWN expression (`allProductsDict[id] || line`, which prefers the line's firestoreId)
      // — a synthetic `{ id }` would probe the wrong doc for Supabase-shape lines whose id differs
      // from their firestoreId, misclassifying a live product as hard-deleted (review defect 2).
      const dictMissedLines: Array<{ id: string; line: any }> = (info.cartItems || []).flatMap(
        (value: any) =>
          value.isComposite && !value.manufacturedBeforeSale
            ? (value.ingredients || [])
                .filter((ing: any) => !allProductsDict[ing.id])
                .map((ing: any) => ({ id: ing.id, line: ing }))
            : !isPseudoLine(value) && !allProductsDict[value.id]
              ? [{ id: value.id, line: value }]
              : [],
      )
      const dictMissedProductIds: string[] = dictMissedLines.map((m) => m.id)

      // Review defect 2: a dict miss splits into two REAL cases and they must diverge:
      //  - the product doc still EXISTS (stale/partial dict — the common ATL-767 class):
      //    restore proceeds; `tx.update` works.
      //  - the product doc was HARD-DELETED: Firestore `update` on a missing doc aborts the
      //    whole idempotent-delete transaction — the sale would become permanently
      //    undeletable. Restoring via set-merge would RECREATE a ghost product doc in the
      //    catalog (a filled-in absence in the source of truth), so the restore is SKIPPED
      //    for exactly those ids instead — loudly. The divergence this leaves is bounded and
      //    principled: Firestore cannot represent stock for a product that no longer exists,
      //    while delete_sale_atomic's upsert restores it onto a soft-deleted product — stock
      //    that is on NO read path, the same class the ATL-777 fidelity gate excludes.
      //    (Checked OUTSIDE the transaction: reads must precede writes inside it, and
      //    `applyWrites` may only write. The check→commit race collapses to the abort case,
      //    which is retriable.)
      const hardDeletedProductIds = new Set<string>()
      for (const { id: missedId, line: missedLine } of dictMissedLines) {
        try {
          // Same ref expression as the restore loop — probing a different doc than the loop
          // would write to is how a live product gets misclassified as hard-deleted.
          const missedRef = getFirestoreRef(allProductsDict[missedId] || missedLine, 'products') as any
          const snap = typeof missedRef?.get === 'function' ? await missedRef.get() : null
          // snapshotExists, NOT `snap.exists === false`: native @react-native-firebase exposes
          // `exists` as a METHOD (web compat: a boolean) — the raw comparison is always false on
          // iOS/Android, exactly where this check matters most (the ATL-570 platform-split class).
          if (snap && !snapshotExists(snap)) hardDeletedProductIds.add(missedId)
        } catch {
          // Unreadable ⇒ assume it exists; a wrong guess aborts the transaction (retriable),
          // never silently skips a restore.
        }
      }

      if (dictMissedProductIds.length > 0) {
        const restored = dictMissedProductIds.filter((id) => !hardDeletedProductIds.has(id))
        const skipped = [...hardDeletedProductIds]
        console.warn(
          '[deleteSale] dict-missing products in stock restore (ATL-767)',
          JSON.stringify({ saleId: ref.id, restored, skippedHardDeleted: skipped }),
        )
        Sentry.captureMessage('deleteSale: stock restore hit products missing from the local dict', {
          level: 'warning',
          tags: { delete_sale_dict_miss: true },
          fingerprint: ['delete-sale-dict-miss'],
          contexts: {
            delete_sale: {
              saleId: ref.id,
              restoredProductIds: restored.slice(0, 20),
              // Skipped = hard-deleted docs: Supabase restores onto the soft-deleted product
              // (no read path), Firestore cannot — a bounded, documented divergence per pair.
              skippedHardDeletedProductIds: skipped.slice(0, 20),
            },
          },
        })
      }

      await runIdempotentDelete(saleRef, 'deleteSale', (batch: any) => {

      // delete individual cart items
      for (const value of info.cartItems || []) {
        if (value.cartItemRef) {
          batch.delete(value.cartItemRef)
        }
      }

      // restore stock
      //
      // ATL-767: restore EVERY line, with or without a dict entry. This loop used to gate each
      // restore on `allProductsDict[...]` membership, silently skipping products deleted from the
      // catalog (or not hydrated) between the sale and its deletion — while delete_sale_atomic
      // reverses every recorded movement. The round-trip comparator proved that residual
      // PERMANENT: Supabase over-stated the product's stock by the sold quantity forever, and no
      // id-level diff could see it (the sale row is gone from both sides). saveNewSale already
      // deducts dict-missing products (ref from the line itself, sales.ts:845) — the delete must
      // be symmetric with the deduction it reverses, not with the catalog's current opinion.
      //
      // ATL-1381: the baseline tracker is built INSIDE this callback on purpose. `applyWrites` may run
      // more than once when the transaction retries, and a tracker hoisted above would carry the
      // abandoned attempt's advanced values into the next one.
      const storeStockField = 'storeStock.' + info.store
      const stockBaseline = stockBaselineTracker(allProductsDict, info.store)
      for (const value of info.cartItems || []) {
        if (value.isComposite && !value.manufacturedBeforeSale) {
          for (const ingredient of value.ingredients || []) {
            // Hard-deleted docs are skipped (see the adjudication above the transaction) —
            // tx.update on a missing doc would abort the whole delete.
            if (!hardDeletedProductIds.has(ingredient.id)) {
              const increment = value.quantity * ingredient.quantity
              const ingredientRef = getFirestoreRef(allProductsDict[ingredient.id] || ingredient, 'products')
              const ingredientChain = stockBaseline.next(ingredient.id, increment)
              batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
              batch.set(ingredientRef.collection('movement').doc(), {
                productID: ingredient.id,
                type: 'deleteSale',
                previousInStock: ingredientChain.previousInStock,
                writeSeq: ingredientChain.writeSeq,
                increment,
                date: fieldValue.serverTimestamp(),
                linkedDocId: ref.id,
                linkedDocDate: fieldValue.serverTimestamp(),
                clientSaveDate: new Date(),
                store: info.store,
                organizationId: info.organizationId,
              })
            }
          }
        } else {
          // Pseudo lines (custom product, delivery) never moved stock at sale time. NOT the raw
          // sentinel check: a Supabase-read sale carries pseudo lines re-keyed to cartItemId
          // (tagPseudoLineForApi), which `isPseudoLine` catches via lineType — the sentinels
          // alone would let those through and abort the transaction on nonexistent refs.
          // Hard-deleted products are skipped per the adjudication above the transaction.
          if (!isPseudoLine(value) && !hardDeletedProductIds.has(value.id)) {
            const increment = value.quantity
            const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
            const lineChain = stockBaseline.next(value.id, increment)
            batch.update(valueRef, { [storeStockField]: fieldValue.increment(increment) })
            batch.set(valueRef.collection('movement').doc(), {
              productID: value.id,
              type: 'deleteSale',
              previousInStock: lineChain.previousInStock,
              writeSeq: lineChain.writeSeq,
              increment,
              date: fieldValue.serverTimestamp(),
              linkedDocId: ref.id,
              linkedDocDate: fieldValue.serverTimestamp(),
              clientSaveDate: new Date(),
              store: info.store,
              organizationId: info.organizationId,
            })
          }
        }
      }

      // reverse client bonuses (including punchCard)
      if (info.client != null && info.paymentMethod) {
        // ATL-911 — `totalPurchasedValue` is reversed ONCE, under the same gate that incremented it.
        //
        // It used to be reversed inside EACH loyalty branch, while the increment on the sale path is
        // gated only on `info.client && info.paymentMethod`. Three outcomes, two of them wrong:
        //
        //   neither flag set  → incremented on sale, NEVER decremented on delete  → total inflates
        //   both flags set    → decremented TWICE                                 → total deflates
        //   exactly one flag  → correct
        //
        // This is the customer-facing Firestore figure, and reads are still served from Firestore —
        // so it is wrong on the screen today, not merely at cutover. It also explains the
        // both-directions client-aggregate samples (fs=0 against sb=139/125/139) that an earlier
        // reading blamed on a NULL payment_method_id; that premise was falsified.
        batch.update(db.collection('clients').doc(info.client.id), {
          totalPurchasedValue: fieldValue.increment(-info.finalTotalCartPrice),
        })
        if (info.cashbackEnabled) {
          batch.update(db.collection('clients').doc(info.client.id), {
            bonusValue: fieldValue.increment(-info.bonusAccumulation + info.bonusReduction),
          })
        }
        if (info.punchCardEnabled) {
          let punchCardValueAccumulation = 0
          ;(info.cartItems || []).forEach((item: any) => {
            if (item.punchCardEnabled) {
              punchCardValueAccumulation += item.quantity
            }
          })
          if (info.punchCardReduction > 0) {
            punchCardValueAccumulation = punchCardValueAccumulation - 1 - (info.punchCardCapacity - 1)
          }
          batch.update(db.collection('clients').doc(info.client.id), {
            punchCardValue: fieldValue.increment(-punchCardValueAccumulation),
          })
        }
      }

      // delete transactions
      if (info.paymentMethod) {
        batch.delete(db.collection('transactions').doc(info.transactionId))
      }
      if (info.secondPaymentMethod) {
        batch.delete(db.collection('transactions').doc(info.secondTransactionId))
      }

      // reverse dailyStats
      //
      // ATL-1013: keyed by the sale's BUSINESS day, not the device's calendar day. saveNewSale
      // writes this doc under `getDailyStatID(store, saleDate)` (store timezone + autoclose
      // cutoff) and stamps the result on the sale as `dailyStatID`, so that stored id is
      // authoritative. Sales written before the field existed (and imported ones) carry none,
      // and the fallback here used to be a bare `format(saleDate, 'd-M-y')` — the DEVICE's day.
      // For a sale in the post-midnight autoclose tail, or a device outside the store's
      // timezone, that names a doc the sale's amounts were never added to: the wrong day is
      // decremented and the real one stays inflated, permanently and silently. Across a month
      // boundary it leaves a MONTH's margin carrying a deleted sale — and when the named doc
      // does not exist at all, `tx.update` raises NOT_FOUND and aborts this whole transaction,
      // so the sale can never be deleted.
      const statDocName = dailyStatIDForRecord(info.dailyStatID, info.store, info.saleDate)
      const saleStatFields = dailyStatIncrementsForSales([info], true, true)
      batch.update(db.collection('dailyStats').doc(statDocName), saleStatFields)

      // log deletion (the record itself is built OUTSIDE the transaction — see the declaration above —
      // because the Supabase audit mirror below needs it after the transaction has committed)
      batch.set(deleteSaleUpdateRef, deleteSaleUpdate)
      })

      // Mirror the audit-log entry to Supabase (best-effort)
      await dualWriteUpdate(deleteSaleUpdate)

      // Dual write to Supabase.
      //
      // ATL-782: the firestoreId context is load-bearing — a delete success purges the sale's
      // queued outbox entries across ALL labels, so a queued create can't resurrect a voided
      // sale on replay. Delete responses carry no firestoreId, so it must come from here.
      //
      // ATL-1194: this used to hand-roll the 404 rule as `status === 404`, which is why the purge
      // above became a hazard rather than a safeguard. Two 404s at this seam are NOT "the sale was
      // already absent" — the ATL-902 dead-host 404 ("Application not found" from a Railway-era
      // bundle, ~50 measured `*.delete` writes lost exactly here) and a route 404 from the real
      // backend ("Cannot DELETE /api/sales/…", i.e. no handler ran). Both were resolving as a
      // success, so the delete was never queued and never reported, AND the sale's existing queued
      // entries were purged — the device erased its own last record of an unfinished write, leaving
      // Supabase holding a sale the customer voided, to return to their books at the read cutover.
      //
      // `bestEffortDualWriteDelete` swallows only `isEntityAbsentFailure`, which excludes both. Note
      // what that costs today: nothing. The current backend answers a genuinely-absent sale with HTTP
      // 200 — `SalesService.deleteSale` returns `{ message: 'Sale not found in Supabase…' }` at
      // sales.service.ts:2507 (retries exhausted), :2514 (no row) and :2540 (the RPC says so) — and
      // this route carries `FirebaseAuthGuard` alone, which refuses with 401 and cannot emit a 404.
      // (Nor a 403: it converts every refusal, `ForbiddenException` included, into
      // `UnauthorizedException` — see the ATL-1137 note on `isForbiddenFailure`.) So the swallow is
      // kept for older deployed revisions, not for this one.
      //
      // ONE CONSEQUENCE THAT HAD TO BE FIXED ALONGSIDE, not merely recorded. The old false success
      // purged this sale's queued `sales.create` as a side effect, so the sale ended up absent on
      // BOTH sides — consistent, by accident. Queueing the delete instead removes that accident, and
      // on its own it would have been a RESURRECTION: the drain replays a document's entries
      // oldest-first, so against a backend answering POST but not DELETE (a rolled-back deploy, a
      // proxy forwarding one verb) the create lands and puts the voided sale back into Supabase while
      // the delete waits, and at MAX_AGE_MS the delete expires and it is permanent. So
      // `enqueueDualWriteFailure` now applies the same supersede rule a LANDED delete has always had:
      // a queued delete speaks for its whole document. Asserted at the transport — that the create is
      // never re-sent — rather than at the queue.
      await bestEffortDualWriteDelete('sales.delete', () => salesApi.deleteSale(ref.id), {
        firestoreId: ref.id,
      })
      try {
        await auditLogApi.createAuditLog({
          tableName: 'sales',
          recordId: ref.id,
          operation: 'DELETE',
          oldValues: info,
          metadata: { cashierUID: uid, type: 'deleteSale' },
        } as Parameters<typeof auditLogApi.createAuditLog>[0])
        console.log('[deleteSale] Audit log created for:', ref.id)
      } catch (auditError: any) {
        console.warn('[deleteSale] Audit log failed:', auditError?.message || auditError)
      }

      // Handle OFD fiscal returns
      if (fiscalOperator && info.paymentMethod) {
        if (fiscalOperator.id === 'light-kassa') {
          await loadLKTokensFromCache(info.store)
          await checkLKTokens(OFDCredentials, info.store)
          const check = await saveFiscalReturn(OFDCredentials, info, info.store)
          if (!check) {
            await setNewLKTokens(OFDCredentials, info.store)
            await saveFiscalReturn(OFDCredentials, info, info.store)
          }
        } else if (fiscalOperator.id === 'wipon-kassa') {
          const check = await saveReturnFromDelete(OFDCredentials, info)
          if (!check) {
            // ATL-1170 review: `getAccessToken` resolves to `undefined` when the login fails, so this
            // retried the fiscal return with `accessToken: undefined` — a request that can only 401.
            // `refreshCredentials` returns null instead, and there is nothing to retry with.
            const refreshed = await refreshCredentials(OFDCredentials)
            if (refreshed) {
              await saveReturnFromDelete(refreshed, info)
            }
          }
        }
      }
    },
    plan: async () => {
      const request = await buildOutboxRequest((client) => createSalesApi(client).deleteSale(ref.id))
      return {
        documentId: ref.id,
        relatedIds: saleRelatedIds(info),
        request,
        effects: null,
        fromAck: () => deletedSaleTail(db, ref, info, uid, fiscalOperator, OFDCredentials),
        // Review 1, S2-b: which of the backend's answers say THIS request voided the sale.
        ackDisposition: saleDeleteAckDisposition,
        // Review 1, S3-3: the sale was already gone on the first send. Master's body skips its whole
        // transaction then — the Firestore `deleteSale` Updates record included — and still runs the
        // audit log and the fiscal return; so does this.
        fromAbsent: () => deletedSaleTail(db, ref, info, uid, fiscalOperator, OFDCredentials, { updatesRecord: false }),
      }
    },
  })

/**
 * The answers `SalesService.deleteSale` gives `DELETE /sales/:id` (200, `{ message }`), pinned by
 * `salesWritersSupabase.p9.test.js` against the backend source:
 *  - `'Sale deleted successfully'` — `delete_sale_atomic` voided it in THIS request: `applied`;
 *  - `'Sale not found in Supabase (may not have been synced yet)'` / `'… (may have already been
 *    deleted)'` — no live sale: `absent`. The backend cannot say whether an earlier request of this
 *    same void, another till's void, or no sync at all made it so (review 1, S2-b: no request identity);
 *  - anything else: `unrecognized` — first answer or re-sent, it ends the void as outcome-unknown
 *    (`WriteWOutcomeUnknownError`, `cause: 'unrecognized'`): no Updates record, no audit, no fiscal
 *    return (review 2, S3-5).
 */
export const SALE_DELETE_APPLIED_MESSAGE = 'Sale deleted successfully'
export const SALE_DELETE_ABSENT_MESSAGE_PREFIX = 'Sale not found in Supabase'
export const saleDeleteAckDisposition = (ack: unknown): WAckDisposition => {
  const message = typeof ack === 'object' && ack !== null && 'message' in ack ? (ack as { message: unknown }).message : null
  if (message === SALE_DELETE_APPLIED_MESSAGE) return 'applied'
  if (typeof message === 'string' && message.startsWith(SALE_DELETE_ABSENT_MESSAGE_PREFIX)) return 'absent'
  return 'unrecognized'
}

/**
 * The NON-W part of a void, run after the server acknowledged `DELETE /sales/:id` in Supabase mode — the
 * three things `deleteSale`'s Firestore body does besides the books (`salesWritersSupabase.p9.test.js` holds
 * the two to the same calls for the same input):
 *  - the `deleteSale` record of the Updates feed (`updates`, not fenced: Firestore first, then its v1
 *    mirror — `saveUpdateRecord`), built as the body builds it;
 *  - the audit log (`POST /audit-log`), best-effort as in the body;
 *  - the fiscal return of a paid sale (light-kassa / wipon-kassa), as in the body.
 * `updatesRecord: false` (the sale was already gone, `fromAbsent`): no Updates record, as the body skips
 * it for a gone sale. Master's body still mirrors that unwritten record to Supabase (`dualWriteUpdate`
 * runs outside the skipped transaction) — a v1 mirror of a Firestore record that does not exist, not
 * reproduced here (§17).
 */
const deletedSaleTail = async (
  db: any,
  ref: MockRef,
  info: any,
  uid: string,
  fiscalOperator: any,
  OFDCredentials: any,
  { updatesRecord = true }: { updatesRecord?: boolean } = {},
): Promise<void> => {
  if (updatesRecord) {
    await saveUpdateRecord(db, {
      date: new Date(),
      cashierUID: uid,
      type: 'deleteSale',
      dataBefore: sanitizeForFirestore(info),
    })
  }
  try {
    await auditLogApi.createAuditLog({
      tableName: 'sales',
      recordId: ref.id,
      operation: 'DELETE',
      oldValues: info,
      metadata: { cashierUID: uid, type: 'deleteSale' },
    } as Parameters<typeof auditLogApi.createAuditLog>[0])
    console.log('[deleteSale] Audit log created for:', ref.id)
  } catch (auditError: any) {
    console.warn('[deleteSale] Audit log failed:', auditError?.message || auditError)
  }
  if (fiscalOperator && info.paymentMethod) {
    if (fiscalOperator.id === 'light-kassa') {
      await loadLKTokensFromCache(info.store)
      await checkLKTokens(OFDCredentials, info.store)
      const check = await saveFiscalReturn(OFDCredentials, info, info.store)
      if (!check) {
        await setNewLKTokens(OFDCredentials, info.store)
        await saveFiscalReturn(OFDCredentials, info, info.store)
      }
    } else if (fiscalOperator.id === 'wipon-kassa') {
      const check = await saveReturnFromDelete(OFDCredentials, info)
      if (!check) {
        const refreshed = await refreshCredentials(OFDCredentials)
        if (refreshed) {
          await saveReturnFromDelete(refreshed, info)
        }
      }
    }
  }
}

/* eslint-enable @typescript-eslint/no-explicit-any */
// for now due to their complexity and deep integration with other modules.
// They will be migrated incrementally.
